import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDownload, faXmark, faImage } from '@fortawesome/free-solid-svg-icons';
import { getFileEntry, setFileEntry } from './fileUrlCache';
import DOMPurify from 'dompurify';

const DisplayMessage = ({ msg, formatDate, apiClient }) => {
    const [fileUrl, setFileUrl] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [isRetrieving, setIsRetrieving] = useState(false);
    const [isFileDeleted, setIsFileDeleted] = useState(false);
    const [isImage, setIsImage] = useState(false);
    const [isImageExpanded, setIsImageExpanded] = useState(false); // New state for expanded image

    // Ref to keep track of whether the file has already been fetched
    const fileFetchedRef = useRef(false);

    // Function to check if file is an image
    const checkIfImage = (name) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
        const extension = name.split('.').pop().toLowerCase();
        return imageExtensions.includes(extension);
    };

    // Function to fetch the file from the server
    const fetchFile = async (force = false) => {
        if (!msg.filedata) return;

        const id = msg.filedata.fileId;
        const nameFromMsg = msg.filedata.name || 'download';

        // 1) Try cache first
        const cached = getFileEntry(id);
        if (cached && !force) {
            setFileName(cached.name || nameFromMsg);
            setFileUrl(cached.url);
            setIsImage(checkIfImage(cached.name || nameFromMsg));
            fileFetchedRef.current = true;
            return;
        }

        // 2) Default UI state before fetch
        setFileName(nameFromMsg);
        setIsImage(checkIfImage(nameFromMsg));

        if (fileFetchedRef.current && !force) return;

        try {
            setIsRetrieving(true);
            const response = await apiClient.get('/files/retrieve-msgfile', {
            params: { fileId: id },
            responseType: 'blob',
            headers: { Accept: '*/*' },
            });

            const ct = (response.headers?.['content-type'] || '').toLowerCase();

            // 3) Server may return JSON/text for “file deleted”
            if (ct.includes('application/json') || ct.startsWith('text/')) {
            const text = await response.data.text();
            if (text?.toLowerCase().includes('file deleted')) {
                setIsFileDeleted(true);
                setFileUrl(null);
                return;
            }
            // Otherwise fall through and still build a blob for download
            }

            // 4) Resolve the final filename (serverName) from Content-Disposition
            const cd = response.headers?.['content-disposition'] || '';
            const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
            let serverName = nameFromMsg;
            if (match) {
            try { serverName = decodeURIComponent(match[1]); }
            catch { serverName = match[1]; }
            }
            setFileName(serverName);
            setIsImage(checkIfImage(serverName));

            // 5) Build blob URL and cache it
            const blobType = ct || (checkIfImage(serverName)
            ? `image/${serverName.split('.').pop().toLowerCase()}`
            : 'application/octet-stream');

            const blob = new Blob([response.data], { type: blobType });
            const url = URL.createObjectURL(blob);

            setFileUrl(url);
            fileFetchedRef.current = true;
            setFileEntry(id, { url, name: serverName, type: blobType });
        } catch (err) {
            console.error('Error fetching file:', err?.message || err);
            setFileUrl(null);
        } finally {
            setIsRetrieving(false);
        }
        };

    // Function to trigger the download
    const handleDownload = async () => {
    // Ensure we have a blob; fetch if needed (or if previous fetch failed)
    if (!fileUrl && !isFileDeleted && !isRetrieving) {
        await fetchFile(true);
    }

    if (!fileUrl || isFileDeleted) return;
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    // Some browsers need a short delay to register the element
    requestAnimationFrame(() => {
        a.click();
        setTimeout(() => {
        document.body.removeChild(a);
        // keep the object URL for preview; revoke later if you want
        }, 0);
    });
    };

    // Function to expand the image
    const handleImageClick = () => {
        setIsImageExpanded(true);
    };

    // Function to close the expanded image modal
    const handleCloseExpandedImage = () => {
        setIsImageExpanded(false);
    };

    // Fetch the file when the message is updated
    useEffect(() => {
        if (msg && msg.filedata !== null && !fileFetchedRef.current) {
            fetchFile();
        }

        return () => {};
    }, [msg]); // Ensure the effect runs when msg changes

    return (
        <div>
            {msg?.filedata?.fileId ? (
                <>
                    <strong>{msg.createdBy}: {formatDate(msg.createdDate)}</strong>
                    <div className="msgfile-message">
                        <div className="msgfile-link">
                            {/* The message content for the file */}
                            <a target="_blank" rel="noopener noreferrer">
                                {msg.content}
                            </a>
                            {isRetrieving ? (
                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '5px' }} />
                            ) : (
                                isFileDeleted ? (
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        style={{ padding: '5px', borderRadius: '5px', marginRight: '5px', backgroundColor: 'red' }}
                                    />
                                ) : (
                                    <div className="file-content">
                                        {/* Display image if it's an image file */}
                                        {isImage && fileUrl && (
                                            <div className="image-preview">
                                                <img
                                                    src={fileUrl}
                                                    alt={fileName}
                                                    style={{ maxWidth: '100%', maxHeight: '100%', marginTop: '10px', cursor: 'pointer' }}
                                                    onClick={handleImageClick}  // Add click handler to expand image
                                                />
                                            </div>
                                        )}
                                        {/* The download button */}
                                        <div className="file-download">
                                            <button
                                                onClick={handleDownload}
                                                className="download-btn"
                                                disabled={isRetrieving || isFileDeleted}
                                                title={isFileDeleted ? 'File deleted' : (isRetrieving ? 'Preparing…' : 'Download')}
                                            >
                                            {isRetrieving ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : (
                                                <FontAwesomeIcon icon={faDownload} />
                                            )}
                                            </button>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <strong>{msg.createdBy}: {formatDate(msg.createdDate)}</strong>
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                </>
            )}
            <span className={`read-status ${msg.messageRead ? '' : 'unread'}`}>
                {msg.messageRead ? '✓ Read' : '✗ Unread'}
            </span>

            {/* Modal for expanded image */}
            {isImageExpanded && (
                <div className="expanded-image-modal" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'zoom-out'
                }} onClick={handleCloseExpandedImage}>
                    <img
                        src={fileUrl}
                        alt={fileName}
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            objectFit: 'contain',
                            cursor: 'zoom-out',
                        }}
                    />
                    <button
                        onClick={handleCloseExpandedImage}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            backgroundColor: 'red',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '10px',
                            cursor: 'pointer',
                            fontSize: '20px',
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default DisplayMessage;
